import React from "react";
import { useTranslation } from "react-i18next";
import SmallOptionContainer from "../../option/smallOptionContainer/SmallOptionContainer";
import Tooltip from "../../tooltip/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleQuestion } from "@fortawesome/pro-regular-svg-icons";

import "./OptionInput.scss";

interface IOption {
    id: string;
    label: string;
    disabled?: boolean;
}

interface IProps {
    filedtitle?: React.ReactNode;
    help?: React.ReactNode;
    options: IOption[];
    /** Render only the icon inside each option (hide the text). */
    iconOnly?: boolean;
    /** Controlled value. If omitted, the first option is selected by default. */
    activeOption?: string;
    /** Callback fired whenever the user picks a different option. */
    onOptionChange?: (optionID: string) => void;
}

const OptionInput: React.FC<IProps> = ({
    filedtitle,
    help,
    options,
    iconOnly = false,
    activeOption: controlledActiveOption,
    onOptionChange,
}) => {
    const { t } = useTranslation();

    const [activeOption, setActiveOption] = React.useState<string>(controlledActiveOption ?? options[0]?.id ?? "");

    React.useEffect(() => {
        if (controlledActiveOption !== undefined) {
            setActiveOption(controlledActiveOption);
        }
    }, [controlledActiveOption]);

    const handleOptionClick = (option: IOption) => {
        if (option.disabled) return;

        if (controlledActiveOption === undefined) {
            setActiveOption(option.id);
        }

        onOptionChange?.(option.id);
    };

    const insertTitleOrHelp = filedtitle || help;

    return (
        <div className="input-field-main-wrapper">
            <div className="input-field-header">
                {insertTitleOrHelp && (
                    <div className="input-field-title-container">
                        {filedtitle && <p>{filedtitle}</p>}
                        {help && (
                            <Tooltip title={help}>
                                <FontAwesomeIcon icon={faCircleQuestion} />
                            </Tooltip>
                        )}
                    </div>
                )}
            </div>
            <div className="userful-options-wrapper" role="radiogroup" aria-label={t("Option selector")}>
                {options.map((option) => (
                    <SmallOptionContainer
                        key={option.id}
                        id={option.id}
                        type="radio"
                        title={iconOnly ? undefined : option.label}
                        value={option.id === activeOption ? "selected" : "unselected"}
                        disabled={option.disabled}
                        onClick={() => handleOptionClick(option)}
                    />
                ))}
            </div>
        </div>
    );
};

export default OptionInput;
export type { IOption };
